// LhFormInner.js
import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getIsLh, setIsLh, getSiteId, setSiteId } from '../../hooks/useLhForm';
import axios from 'axios';
import config from '../../config';
import { Modal, Button, Table, Form } from 'react-bootstrap';

export default function LhSetting() {
  const Iv_list = useSelector((state) => state.Iv_list || {});
  const Jb_list = useSelector((state) => state.Jb_list || {});
  const Ev_list = useSelector((state) => state.Ev_list || {});

  // ✅ 장비 내용이 바뀌었는지 안정적으로 감지하기 위한 키
  const devicesKey = useMemo(() => {
    const pick = (obj, keys) =>
      keys.reduce((acc, k) => (acc[k] = obj?.[k] ?? [], acc), {});
    const iv = pick(Iv_list, ['iv_address', 'iv_name']);
    const jb = pick(Jb_list, ['jb_address', 'jb_name']);
    const ev = pick(Ev_list, ['ev_address', 'ev_name']);
    return JSON.stringify({ iv, jb, ev });
  }, [Iv_list, Jb_list, Ev_list]);

  const allAddresses = useMemo(() => {
    const ivAddresses = (Iv_list.iv_address || []).map((addr, i) => ({
      id: `IV:${Number(addr)}`,                         // 👈 안정적인 ID 추가
      address: Number(addr),
      name: Iv_list.iv_name?.[i] || '이름없음',
      type: '인버터',
    }));
    const jbAddresses = (Jb_list.jb_address || []).map((addr, i) => ({
      id: `JB:${Number(addr)}`,                         // 👈 동일 주소라도 타입 다르면 별도 ID
      address: Number(addr),
      name: Jb_list.jb_name?.[i] || '이름없음',
      type: '접속반',
    }));
    const evAddresses = (Ev_list.ev_address || []).map((addr, i) => ({
      id: `EV:${Number(addr)}`,
      address: Number(addr),
      name: Ev_list.ev_name?.[i] || '이름없음',
      type: '환경센서',
    }));

    const merged = [...ivAddresses, ...jbAddresses, ...evAddresses];

    // 👇 ID 기준으로 유니크 처리(기존 주소 기준 제거)
    const uniqueById = Array.from(
      new Map(merged.map(item => [item.id, item])).values()
    ).filter(item => item.address != null);

    const sorted = uniqueById.sort((a, b) => a.address - b.address);

    // 기본 라벨은 인덱스로 부여(저장된 값이 있으면 이후 단계에서 유지/우선)
    return sorted.map((item, index) => ({
      ...item,
      label: String(index + 1).padStart(4, '0'),
    }));
  }, [Iv_list, Jb_list, Ev_list]);

  const [showModal, setShowModal] = useState(false);
  const [isLhState, setIsLhState] = useState(getIsLh());
  const [siteIdState, setSiteIdState] = useState(getSiteId());

  const [lastSendTime, setLastSendTime] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedAddresses, setEditedAddresses] = useState([]);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const res = await axios.get(`${config.backendUrl}/load-lh-labels`);
        const { labels, site_id, is_lh } = res.data;

        let mergedLabels;

        if (Array.isArray(labels)) {
          // 저장된 라벨을 id 기준으로 매핑
          const toId = (type, address) => {
            const prefix = type?.startsWith('인버터')
              ? 'IV'
              : type?.startsWith('접속반')
              ? 'JB'
              : 'EV';
            return `${prefix}:${Number(address)}`;
          };

          const savedMap = new Map(
            labels.map(item => {
              const id = toId(item.type, item.address);
              return [id, { ...item, id, address: Number(item.address) }];
            })
          );

          // 현재 존재하는 장비들 중 이미 사용 중인 라벨 수집
          const usedLabels = new Set(
            allAddresses
              .map(a => savedMap.get(a.id)?.label)
              .filter(Boolean)
          );

          // 비어 있는 가장 작은 번호를 찾아 배정 (대물림 방지)
          const allocateLabel = () => {
            let n = 1;
            while (usedLabels.has(String(n).padStart(4, '0'))) n += 1;
            const label = String(n).padStart(4, '0');
            usedLabels.add(label);
            return label;
          };

          // 전체 장비 목록을 기준으로 병합: 기존 라벨은 유지, 새 장비만 새 라벨
          mergedLabels = allAddresses.map(item => {
            const saved = savedMap.get(item.id);
            return {
              id: item.id,
              address: item.address,
              name: item.name,
              type: item.type,
              label: saved?.label ?? allocateLabel(),
            };
          });
        } else {
          // 저장값이 없으면 기본 라벨 사용
          mergedLabels = allAddresses;
        }

        setEditedAddresses(mergedLabels);
        setIsLhState(is_lh ?? false);
        setSiteIdState(site_id ?? '');
        setIsLh(is_lh ?? false);
        setSiteId(site_id ?? '');

      } catch (e) {
        console.warn('라벨 로딩 실패, 자동 생성 라벨 사용');
        setEditedAddresses(allAddresses);
      }
    };

    // ✅ 장비 내용이 바뀌면 재로드되도록 devicesKey를 의존성에 추가
    fetchLabels();
  }, [devicesKey, allAddresses]);

  const handleSubmit = async () => {
    try {
      // 저장 시 불필요한 필드(id 등) 제외
      const payloadLabels = editedAddresses.map(({ address, label, type, name }) => ({
        address,
        label,
        type,
        name,
      }));

      await axios.post(`${config.backendUrl}/save-lh-labels`, {
        site_id: siteIdState,
        is_lh: isLhState,
        labels: payloadLabels,
      });

      if (isLhState) {
        alert('설정이 완료되었습니다.');
      } else {
        alert('LH 연동이 비활성화되었습니다.');
      }
    } catch (e) {
      alert('전송 실패!');
    }
  };

  const fetchLastSendTime = async () => {
    try {
      const res = await axios.get(`${config.backendUrl}/api/last_lh_send_time`);
      setLastSendTime(res.data.last_send_time);
    } catch (e) {
      console.error('불러오기 실패', e);
      setLastSendTime('불러오기 실패');
    }
  };

  const handleLabelChange = (index, newValue) => {
    const updated = [...editedAddresses];
    updated[index].label = newValue;
    setEditedAddresses(updated);
  };

  const handleSave = async () => {
    try {
      const refinedLabels = editedAddresses.map(({ address, label, type, name }) => ({
        address,
        label,
        type,
        name,
      }));

      const response = await fetch(`${config.backendUrl}/save-lh-labels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          site_id: siteIdState,
          is_lh: isLhState,
          labels: refinedLabels,
        }),
      });

      if (response.ok) {
        alert('저장 완료');
        setEditMode(false);
        setIsLh(isLhState);
        setSiteId(siteIdState);
      } else {
        alert('저장 실패');
      }
    } catch (err) {
      console.error(err);
      alert('저장 중 오류 발생');
    }
  };

  return (
    <div className="LhBox">
      <div className="row justify-content-center text-center">
        <div className="col-md-3 d-flex align-items-center">
          <h5 className="mb-0 me-2">LH 설정</h5>
          <input
            type="checkbox"
            checked={isLhState}
            onChange={(e) => {
              setIsLhState(e.target.checked);
              setIsLh(e.target.checked);
            }}
            style={{
              width: '24px',
              height: '24px',
              marginRight: '8px',
              verticalAlign: 'middle',
            }}
          />
          <span style={{ fontSize: '16px' }}>
            {isLhState ? '활성화 완료' : '활성화 하기'}
          </span>
        </div>

        <div className="col-md-2">
          <h5>단지번호</h5>
          <input
            type="text"
            className="form-control"
            style = {{width: "215px"}}
            value={siteIdState}
            onChange={(e) => {
              setSiteIdState(e.target.value);
              setSiteId(e.target.value);
            }}
            disabled={!isLhState}
            placeholder={!isLhState ? 'LH 설정을 활성화 하세요' : ''}
          />
        </div>

        <div className="col-md-3 d-flex align-items-center justify-content-center" style={{ gap: '10px' }}>
          <button className="btn btn-warning" onClick={handleSubmit}>저장하기</button>
          <Button variant="info" onClick={() => {
            if (!isLhState) {
              alert('LH 설정 활성 여부를 확인해주세요.');
              return;
            }
            setShowModal(true);
          }}>설정 보기</Button>
        </div>

        <div className="col-md-3 d-flex align-items-center justify-content-center" style={{ gap: '10px' }}>
          <Button variant="secondary" onClick={fetchLastSendTime}>⟲</Button>
          <span>{lastSendTime ? `마지막 수신시간 ${lastSendTime}` : '마지막 수신시간'}</span>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        dialogClassName="custom-modal-width"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>LH 설정 확인</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>단지번호:</strong> {siteIdState}
          </p>

          <div className="modal-actions">
            {!editMode ? (
              <Button variant="primary" onClick={() => setEditMode(true)}>
                수정하기
              </Button>
            ) : (
              <>
                <Button variant="success" onClick={handleSave} style={{ marginRight: '8px' }}>
                  저장
                </Button>
                <Button variant="secondary" onClick={() => setEditMode(false)}>
                  취소
                </Button>
              </>
            )}
          </div>

          <Table bordered hover className="custom-table text-center">
            <thead>
              <tr>
                <th>#</th>
                <th>장비 주소</th>
                <th>장비 이름</th>
                <th>장비 타입</th>
                <th>장비 일련번호(단지번호 + 일련번호)</th>
              </tr>
            </thead>
            <tbody>
              {editedAddresses.map((item, idx) => (
                <tr key={item.id || `${item.type}:${item.address}`}> {/* 👈 index 대신 안정 키 */}
                  <td>{idx + 1}</td>
                  <td>{item.address}</td>
                  <td>{item.name}</td>
                  <td>{item.type}</td>
                  <td>
                    {editMode ? (
                      <Form.Control
                        type="text"
                        value={item.label}
                        onChange={(e) => handleLabelChange(idx, e.target.value)}
                        size="sm"
                        className="text-center"
                      />
                    ) : (
                      item.label
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
